import {useEffect, useRef, useState} from "react";

type ScrollDirection = "up" | "down";

type Params = {
    enabled?: boolean;

    /**
     * Минимальное изменение scroll, после которого обновляется direction
     */
    threshold?: number;

    /**
     * С какого scrollY считаем, что страница проскроллена
     */
    scrolledOffset?: number;
};

type Return = {
    scrollDirection: ScrollDirection;
    scrollY: number;
    isScrolled: boolean;
};

export const useScrollDirection = (
    {
        enabled = true,
        threshold = 5,
        scrolledOffset = 32,
    }: Params = {}
): Return => {
    const [scrollDirection, setScrollDirection] = useState<ScrollDirection>("up");
    const [scrollY, setScrollY] = useState(0);

    const lastScrollY = useRef(0);
    const ticking = useRef(false);

    useEffect(() => {
        if (!enabled) return;

        const handleScroll = () => {
            const currentScrollY = window.scrollY;

            if (ticking.current) return;

            ticking.current = true;

            window.requestAnimationFrame(() => {
                const diff = currentScrollY - lastScrollY.current;

                setScrollY(currentScrollY);

                if (Math.abs(diff) >= threshold) {
                    const nextDirection = diff > 0 ? "down" : "up";

                    setScrollDirection((prev) =>
                        prev !== nextDirection ? nextDirection: prev
                    );

                    lastScrollY.current = currentScrollY;
                }

                ticking.current = false;
            });
        };

        /**
         * Инициализация
         */
        setScrollY(window.scrollY);

        window.addEventListener("scroll", handleScroll, {passive: true});

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [enabled, threshold]);

    return {
        scrollDirection,
        scrollY,
        isScrolled: scrollY > scrolledOffset,
    };
};
